const TOP_SKUS = Object.freeze([
  'fc4top',
  'fc6top',
  'fc10top',
  '10topsoloallenamento',
  'prot1top',
  'protocollo1top',
  'prot2top',
  'protocollo2top',
  'prot3top',
  'protocollo3top',
]);

const normalizeTopSku = (value) => String(value || '').toLowerCase().trim().replace(/[^a-z0-9]/g, '');

export const hasTopSkuMatch = (value) => {
  const normalized = normalizeTopSku(value);
  if (!normalized) return false;
  return TOP_SKUS.some((sku) => normalized.includes(sku));
};

const toDateSafe = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  }
  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const str = String(value).trim();
  if (!str) return null;
  const normalized = str.includes('T') ? str : str.replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const resolveOrderTimeMs = (order = {}) => {
  const candidates = [
    order.orderDate,
    order.order_date,
    order.importedAt,
    order.updatedAt,
    order.updated_at,
    order.createdAt,
    order.created_at,
    order.date_created,
    order.timestamp,
  ];
  for (const candidate of candidates) {
    const parsed = toDateSafe(candidate);
    if (parsed) return parsed.getTime();
  }
  return 0;
};

export const orderHasTopProduct = (order = {}) => {
  if (!order || typeof order !== 'object') return false;

  const orderCandidates = [
    order.productSku,
    order.productId,
    order.productName,
    order.title,
    order.name,
    order.orderTitle,
    order.orderName,
  ];

  if (orderCandidates.some(hasTopSkuMatch)) return true;

  const products = Array.isArray(order.products) ? order.products : [];
  for (const product of products) {
    const productCandidates = [
      product?.sku,
      product?.productSku,
      product?.productId,
      product?.id,
      product?.name,
      product?.title,
      product?.productName,
    ];
    if (productCandidates.some(hasTopSkuMatch)) return true;
  }

  const lineItems = Array.isArray(order.line_items) ? order.line_items : [];
  for (const item of lineItems) {
    const itemCandidates = [item?.sku, item?.product_id, item?.name, item?.title];
    if (itemCandidates.some(hasTopSkuMatch)) return true;
  }

  return false;
};

export const isPaidOrder = (order = {}) => {
  const statusRaw = String(order.status || order.orderStatus || '').toLowerCase();
  const paymentStatus = String(order.paymentStatus || order.payment_status || '').toLowerCase();
  const method = String(order.paymentMethod || order.payment_method || '').toLowerCase();

  const blockedStatuses = new Set([
    'cancelled',
    'wc-cancelled',
    'refunded',
    'wc-refunded',
    'pending',
    'wc-pending',
    'on-hold',
    'wc-on-hold',
    'cancellato',
    'annullato',
    'in attesa di pagamento',
    'attesa di pagamento',
    'pagamento in attesa',
    'rimborsato',
    'rimborsato completamente',
  ]);
  if (blockedStatuses.has(statusRaw)) return false;
  if (statusRaw.includes('cancelled') || statusRaw.includes('refunded')) return false;
  if (statusRaw.includes('cancellat') || statusRaw.includes('annullat')) return false;
  if (statusRaw.includes('rimborsat') || statusRaw.includes('rimborso')) return false;
  if (statusRaw.includes('attesa') && statusRaw.includes('pagament')) return false;
  if (statusRaw.includes('pending') && !statusRaw.includes('completed')) return false;

  const paidStatuses = new Set([
    'pagato',
    'rateizzato',
    'paid',
    'completed',
    'complete',
    'processing',
    'wc-completed',
    'wc-processing',
    'direct_purchase',
    'payment_completed',
    'completed_payment',
  ]);
  if (paidStatuses.has(statusRaw)) return true;
  if (statusRaw.startsWith('wc-') && (statusRaw.includes('completed') || statusRaw.includes('processing'))) return true;
  if (statusRaw.includes('direct') && statusRaw.includes('purchase')) return true;
  if (statusRaw.includes('payment') && statusRaw.includes('complete')) return true;

  const paidPaymentStatuses = new Set(['paid', 'completed', 'complete', 'captured', 'succeeded', 'settled']);
  if (paymentStatus.includes('rimborsat') || paymentStatus.includes('rimborso')) return false;
  if (paymentStatus.includes('attesa') && paymentStatus.includes('pagament')) return false;
  if (paidPaymentStatuses.has(paymentStatus)) return true;
  if (paymentStatus.includes('completed') || paymentStatus.includes('captured')) return true;

  return method === 'contanti' || method === 'cash' || method === 'custom_cash';
};

export const buildTopSkuFields = (order = {}, options = {}) => {
  const topSkuHit = orderHasTopProduct(order);
  if (!topSkuHit) return { topSkuHit: false, topSkuOrderAt: 0 };

  let topSkuOrderAt = resolveOrderTimeMs(order);
  if (!topSkuOrderAt && options.fallbackToNow) {
    topSkuOrderAt = Date.now();
  }

  return { topSkuHit: true, topSkuOrderAt: topSkuOrderAt || 0 };
};

export const TOP_SKUS_LIST = TOP_SKUS;
